import {
	type FormField,
	FormFieldType,
	FormFieldValidator
} from '@semanticedge/persistence-service-noheaders-client/src'
import type { DetailedSelectItem, DateSelection } from '@repo/commons/models'

// Types
export interface FormSchema {
	fields: Array<FormField>
}

export type FormData = Record<string, any>
export type FormErrors = Record<string, string>

// Validation functions
export const validators: Record<FormFieldValidator, (value: any) => string> = {
	[FormFieldValidator.Email]: (value: string): string => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(value) ? '' : 'Please enter a valid email address'
	},
	[FormFieldValidator.Iban]: (value: string): string => {
		// Basic IBAN validation (you might want to use a proper IBAN library)
		const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/
		return ibanRegex.test(value.replace(/\s/g, '')) ? '' : 'Please enter a valid IBAN'
	},
	[FormFieldValidator.Phone]: (value: string): string => {
		// Basic phone validation
		const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
		return phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))
			? ''
			: 'Please enter a valid phone number'
	},
	[FormFieldValidator.Address]: (value: string): string => {
		return value.trim().length >= 5 ? '' : 'Please enter a valid address'
	},
	[FormFieldValidator.FutureDate]: (value: string | DateSelection): string => {
		let dateToCheck: Date | null = null

		if (typeof value === 'string') {
			dateToCheck = new Date(value)
		} else if (value && 'date' in value) {
			dateToCheck = value.date
		}

		if (!dateToCheck) return ''

		const today = new Date()
		today.setHours(0, 0, 0, 0)
		return dateToCheck > today ? '' : 'Please select a future date'
	},
	[FormFieldValidator.PastDate]: (value: string | DateSelection): string => {
		let dateToCheck: Date | null = null

		if (typeof value === 'string') {
			dateToCheck = new Date(value)
		} else if (value && 'date' in value) {
			dateToCheck = value.date
		}

		if (!dateToCheck) return ''

		const today = new Date()
		today.setHours(23, 59, 59, 999)
		return dateToCheck < today ? '' : 'Please select a past date'
	}
}

// Field ID generation
export function getFieldId(field: FormField): string {
	return field.associatedTopicId || 'field_' + Math.random().toString(36).substring(2, 9)
}

// Field validation
export function validateField(field: FormField, value: any): string {
	if (field.required) {
		if (
			value === '' ||
			value === null ||
			value === undefined ||
			(typeof value === 'object' && value !== null && Object.values(value).every((v) => !v))
		) {
			return 'This field is required'
		}
	}

	// Skip further validation if field is empty and not required
	if (!value || value === '') return ''

	// Apply field-specific validators
	if (field.formFieldValidators) {
		for (const validatorType of field.formFieldValidators) {
			const validator = validators[validatorType]
			if (validator) {
				const error = validator(value)
				if (error) return error
			}
		}
	}

	return ''
}

// Label handling
export function getFieldLabel(field: FormField, locale: string = 'en'): string {
	if (field.labelMap && field.labelMap[locale]) {
		return field.labelMap[locale]
	}
	// Fallback to any available label or empty string
	return field.labelMap ? Object.values(field.labelMap)[0] || '' : ''
}

// Form data initialization
export function initializeFormData(schema: FormSchema): FormData {
	const initialData: FormData = {}

	schema.fields.forEach((field) => {
		const fieldId = getFieldId(field)

		// Set default values based on field type
		switch (field.formFieldType) {
			case FormFieldType.Checkbox:
				initialData[fieldId] = false
				break
			case FormFieldType.DatePicker:
			case FormFieldType.TimePicker:
			case FormFieldType.DateTimePicker:
				initialData[fieldId] = undefined
				break
			case FormFieldType.DateRangePicker:
			case FormFieldType.DateTimeRangePicker:
			case FormFieldType.TimeRangePicker:
				initialData[fieldId] = undefined
				break
			case FormFieldType.ExclusiveSelect:
				initialData[fieldId] = shouldShowMultipleSelection(field) ? [] : null
				break
			default:
				initialData[fieldId] = ''
		}
	})

	return initialData
}

// Select field utilities
export function getSelectItems(field: FormField): DetailedSelectItem[] {
	return (
		field.possibleSelections?.map((selection) => ({
			id: selection,
			title: selection,
			description: null,
			imgPayload: null
		})) || []
	)
}

export function getSelectedSelectItem(
	field: FormField,
	formData: FormData
): DetailedSelectItem | null {
	if (shouldShowMultipleSelection(field)) {
		return null // For multi-select, we don't use selectedItem
	}

	const fieldId = getFieldId(field)
	const value = formData[fieldId]
	if (!value) return null

	const items = getSelectItems(field)
	return items.find((item) => item.id === value) || null
}

export function getSelectedSelectItems(field: FormField, formData: FormData): DetailedSelectItem[] {
	if (!shouldShowMultipleSelection(field)) {
		return [] // For single select, we don't use selectedItems
	}

	const fieldId = getFieldId(field)
	const values = formData[fieldId] || []
	if (!Array.isArray(values)) return []

	const items = getSelectItems(field)
	return values
		.map((value) => items.find((item) => item.id === value))
		.filter(Boolean) as DetailedSelectItem[]
}

export function shouldShowMultipleSelection(field: FormField): boolean {
	return field.chooseUpTo ? field.chooseUpTo > 1 : false
}

export function getMaxSelections(field: FormField): number | null {
	return field.chooseUpTo && field.chooseUpTo > 1 ? field.chooseUpTo : null
}

// Form validation
export function validateForm(
	schema: FormSchema,
	formData: FormData
): {
	isValid: boolean
	errors: FormErrors
} {
	const errors: FormErrors = {}
	let isValid = true

	schema.fields.forEach((field) => {
		const fieldId = getFieldId(field)
		const error = validateField(field, formData[fieldId])
		if (error) {
			errors[fieldId] = error
			isValid = false
		}
	})

	return { isValid, errors }
}
